import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { Button, Form, Tab, Table, Tabs } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useRevalidator, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { GameFeatureFlags, GamePublicationState, GamePublicationWishlistState, GameRelationshipKind } from "@/const";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { GamePublicationStateProgressBar, GamePublicationWishlistStateProgressBar } from "@components/Game/Misc";
import { Awesome, DevModal, Hint } from "@components/Helpers";
import { ModerationFieldsList } from "@components/Moderation/ModerationFieldsList";
import { ModeratorCommentsForDeveloper } from "@components/Moderation/ModeratorCommentsForDeveloper";
import { CommonCard } from "@components/Pages";
import { ModeratorCommentsEmbedded } from "@components/Screen/ModeratorComments";
import { parseAndLinkify, scroll_to_top, url_reverse } from "@components/Utils";

export function Publication(props) {
    const commonInfo = useRouteLoaderData("commonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (gameCommonInfo.game.is_bundle && gameCommonInfo.game.publication_state === GamePublicationState.UNPUBLISHED) {
        return null;
    }

    if (commonInfo.account.is_super || commonInfo.account.read_catalog) {
        return <ModeratorScreen {...props} />;
    }
    return <DeveloperScreen {...props} />;
}

const wishlistMode = (game) => {
    return Boolean(game.is_main_client || game.is_dlc);
};

const buildPNA = (pna, wishlistPublication) => {
    const newPNA = { ...pna };
    if (wishlistPublication) {
        delete newPNA.prices;
        delete newPNA.client;
        delete newPNA.live_client;
    }
    return newPNA;
};

function InfoTable({ apiData, step }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    let type = "";
    if (gameCommonInfo.game.is_main_client) {
        type = t("Клиентская");
    } else if (gameCommonInfo.game.is_main_browser) {
        type = t("Браузерная");
    } else if (gameCommonInfo.game.is_demo) {
        type = "Demo";
    } else if (gameCommonInfo.game.is_bundle) {
        type = t("Набор");
    } else if (gameCommonInfo.game.is_dlc) {
        type = "DLC";
    }

    const mapp = {
        type: [t("Тип игры"), type],
        subtype: [t("Подтип"), apiData.game_get_type_display],
        edok: [t("Набор ЕдОк merchants"), gameCommonInfo.game.merchant_set.name],
        residence_terr: [t("Территории резидентства"), apiData.developer_get_residence_terr_display],
        payments_stopped: [t("Платежи остановлены"), gameCommonInfo.game.payments_stopped ? t("Да") : t("Нет")],
        developer_country: [t("Страна"), gameCommonInfo.developer_country_name],
        countries: [t("Страны"), apiData.game_countries],
        base_locale: [t("Базовая локаль"), gameCommonInfo.game.base_locale],
        locales: [t("Какие локали заполнены"), apiData.game_locales_names],
        my_tracker_project_id: [t("ID проекта в myTracker"), gameCommonInfo.game.my_tracker_project_id],
        my_tracker_app_id: [t("ID приложения в myTracker"), gameCommonInfo.game.my_tracker_app_id],
        adult_content: [
            t("Взрослый контент"),
            gameCommonInfo.game.special_flags.includes("erotica") ? t("Да") : t("Нет"),
        ],
        age_limit: [t("Возрастное ограничение по классификации РФ"), gameCommonInfo.game.age_limit],
        use_steam_api: [t("Эмуляция Steam"), apiData.use_steam_api ? t("Да") : t("Нет")],
        game_publishers: [
            t("Издатели"),
            Object.entries(apiData.game_publishers).map(([k, v]) => {
                return (
                    <div key={k} style={{ color: v ? "green" : "red" }}>
                        {k}
                    </div>
                );
            }),
        ],
        game_gamedevs: [
            t("Разработчики"),
            Object.entries(apiData.game_gamedevs).map(([k, v]) => {
                return (
                    <div key={k} style={{ color: v ? "green" : "red" }}>
                        {k}
                    </div>
                );
            }),
        ],
        date_release_estimated: [t("Предполагаемая дата выпуска"), gameCommonInfo.game.date_release_estimated],
        is_laboratory: [t("Лаборатория"), gameCommonInfo.game.is_laboratory ? t("Да") : t("Нет")],
        monetization_model: [t("Модель монетизации"), gameCommonInfo.game.monetization_model],
    };

    const fields =
        step === "tech"
            ? [
                  "type",
                  "monetization_model",
                  "subtype",
                  "developer_country",
                  "edok",
                  "residence_terr",
                  "payments_stopped",
                  "base_locale",
                  "locales",
                  "my_tracker_project_id",
                  "my_tracker_app_id",
                  "game_publishers",
                  "game_gamedevs",
                  "date_release_estimated",
                  "is_laboratory",
              ]
            : step === "done"
              ? [
                    "type",
                    "monetization_model",
                    "subtype",
                    "use_steam_api",
                    "edok",
                    "residence_terr",
                    "developer_country",
                    "base_locale",
                    "locales",
                    "countries",
                    "my_tracker_project_id",
                    "my_tracker_app_id",
                    "age_limit",
                    "adult_content",
                    "game_publishers",
                    "game_gamedevs",
                    "date_release_estimated",
                ]
              : [
                    "type",
                    "monetization_model",
                    "subtype",
                    "countries",
                    "my_tracker_project_id",
                    "my_tracker_app_id",
                    "age_limit",
                    "adult_content",
                ];

    return (
        <>
            <Table bordered size="sm" className="mt-5">
                <tbody>
                    {fields.map((f, ind) => (
                        <tr key={ind}>
                            <th style={{ width: "40%" }}>{mapp[f][0]}</th>
                            <td>{mapp[f][1]}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <DevForm.Field prefix="main" name="manual_publication_data" />
        </>
    );
}

function ModeratorScreen({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(url_reverse("api:game:publication:moderator", { game_id: gameId }));
    const revalidator = useRevalidator();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const show_tab = useActiveTab(gameCommonInfo.game);
    const onFormSubmit = useSubmit();
    const { api_post } = useRequest();

    if (!apiData) return;

    const onModerSubmit = (ev) => {
        onFormSubmit(ev, {
            onOK: (resp) => {
                setApiData(resp);
                revalidator.revalidate();
            },
        });
    };

    const doArchive = (ev) => {
        api_post(ev, url_reverse("api:dashboard:archive"), {
            params: {
                action: ev.target.name,
                entity_id: gameId,
                entity_type: "game",
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    const newApiData = { ...apiData };
                    newApiData.in_archive = !apiData.in_archive;
                    setApiData(newApiData);
                }
            },
        });
    };

    const headerTools = (
        <>
            {apiData.in_archive ? (
                <DevForm.Button variant="dark" size="sm" name="from" onClick={doArchive}>
                    {t("Достать из архива дашборда")}
                </DevForm.Button>
            ) : (
                <DevForm.Button variant="dark" size="sm" name="to" onClick={doArchive}>
                    {t("В архив дашборда")}
                </DevForm.Button>
            )}
        </>
    );

    return (
        <CommonCard header={header} headerTools={headerTools}>
            {show_tab ? (
                <Tabs
                    defaultActiveKey={show_tab}
                    transition={false}
                    className="react-bootstrap-tabs-fix"
                    unmountOnExit={true}
                >
                    <Tab eventKey="project" title={t("Публикация проекта")}>
                        <ModeratorProjectPublicationBlock onModerSubmit={onModerSubmit} />
                    </Tab>
                    <Tab eventKey="wishlist" title={t("Публикация страницы")}>
                        <div className="dmg-callout dmg-callout-info">
                            {t("Для публикации страницы игры без отправки билда на проверку")}
                        </div>

                        <ModeratorWishlistPublicationBlock onModerSubmit={onModerSubmit} />
                    </Tab>
                </Tabs>
            ) : (
                <ModeratorProjectPublicationBlock onModerSubmit={onModerSubmit} />
            )}
        </CommonCard>
    );
}

function ModeratorProjectPublicationBlock({ onModerSubmit }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const commonInfo = useRouteLoaderData("commonInfo");
    const branch = "project";
    const pta_hint = t(
        'Проект будет опубликован сразу после прохождения проверки. В поле "Дата выхода игры" будет проставлена дата фактической публикации проекта на площадке VK Play.'
    );
    const ptm_hint = t("Точные дату и время релиза нужно выбрать самостоятельно после проверки.");

    const fldChange = (ev) => {
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.publication_type = ev.target.value;
        setApiData(newApiData);
    };

    const show_project_progressbar = [
        GamePublicationState.CONFIGURATION_MARKETING,
        GamePublicationState.TESTING,
        GamePublicationState.TESTING_MARKETING,
        GamePublicationState.DONE,
    ].includes(gameCommonInfo.game.publication_state);

    useEffect(() => {
        if (apiData.forms_data) {
            const newApiData = { ...apiData };
            newApiData.forms_data.main.data.moder_comment = "";
            setApiData(newApiData);
        }
    }, [apiData.forms_data?.main.fields.moder_comment.initial]);

    if (
        ![GamePublicationWishlistState.CONFIGURATION, GamePublicationWishlistState.DONE].includes(
            gameCommonInfo.game.publication_wishlist_state
        )
    ) {
        return <AlreadyInModerationTextBlock />;
    }

    const pna = buildPNA(apiData.publication_not_allowed, false);

    return (
        <>
            <PublicationNotAllowedBlock pna={pna} />
            {show_project_progressbar && (
                <div className="d-flex">
                    <ModeratorPublicationMethodLabel branch={branch} />
                    <div className="w-100">
                        <GamePublicationStateProgressBar />
                    </div>
                    <ModeratorCancelPublicationButton branch={branch} />
                </div>
            )}
            <Form onSubmit={onModerSubmit}>
                {[GamePublicationState.CONFIGURATION, GamePublicationState.UNPUBLISHED].includes(
                    gameCommonInfo.game.publication_state
                ) && (
                    <>
                        {commonInfo.account.is_super && (
                            <div className="my-2">
                                <div>
                                    <span style={{ display: "inline-block" }}>
                                        <DevForm.Checkbox
                                            type="radio"
                                            prefix="main"
                                            id="publication_type_auto"
                                            name="publication_type"
                                            value="auto"
                                            checked={
                                                apiData.forms_data.main.data.publication_type === "auto" ||
                                                !apiData.forms_data.main.data.publication_type
                                            }
                                            label={t("Автоматическая публикация")}
                                            onChange={fldChange}
                                        />
                                    </span>{" "}
                                    <Hint tooltip={pta_hint} />
                                </div>
                                <div>
                                    <span style={{ display: "inline-block" }}>
                                        <DevForm.Checkbox
                                            type="radio"
                                            prefix="main"
                                            id="publication_type_manual"
                                            name="publication_type"
                                            value="manual"
                                            checked={apiData.forms_data.main.data.publication_type === "manual"}
                                            label={t("Ручная публикация")}
                                            onChange={fldChange}
                                        />
                                    </span>{" "}
                                    <Hint tooltip={ptm_hint} />
                                </div>
                            </div>
                        )}
                        <DevForm.Button variant="primary" type="submit" name="action" value="start_project_moderation">
                            {t("Отправить на проверку")}
                        </DevForm.Button>
                    </>
                )}

                {[GamePublicationState.TESTING, GamePublicationState.TESTING_MARKETING].includes(
                    gameCommonInfo.game.publication_state
                ) && <ModerationBlock onModerSubmit={onModerSubmit} branch={branch} />}
            </Form>
            {gameCommonInfo.game.publication_state === GamePublicationState.DONE && (
                <>
                    {commonInfo.account.is_super && apiData?.forms_data?.main?.data?.publication_type === "manual" && (
                        <Form onSubmit={onModerSubmit}>
                            <DevForm.Field prefix="main" name="date_launch" />
                            <DevForm.Button variant="primary" type="submit" name="save">
                                {t("Сохранить")}
                            </DevForm.Button>
                        </Form>
                    )}
                </>
            )}

            {gameCommonInfo.game.publication_state === GamePublicationState.TESTING && (
                <>
                    <InfoTable step="tech" apiData={apiData} />
                    <ModeratorCommentsEmbedded gameId={gameCommonInfo.game.id} filter="testing" />
                </>
            )}
            {gameCommonInfo.game.publication_state === GamePublicationState.TESTING_MARKETING && (
                <>
                    <InfoTable step="mark" apiData={apiData} />
                    <ModeratorCommentsEmbedded gameId={gameCommonInfo.game.id} filter="marketing" />
                </>
            )}
            {gameCommonInfo.game.publication_state === GamePublicationState.DONE && (
                <InfoTable step="done" apiData={apiData} />
            )}
        </>
    );
}

function ModeratorWishlistPublicationBlock({ onModerSubmit }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const commonInfo = useRouteLoaderData("commonInfo");
    const branch = "wishlist";
    const pta_hint = t(
        'Проект будет опубликован сразу после прохождения проверки. В поле "Дата выхода игры" будет проставлена дата фактической публикации проекта на площадке VK Play.'
    );
    const ptm_hint = t("Точные дату и время релиза нужно выбрать самостоятельно после проверки.");

    const fldChange = (ev) => {
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.publication_type_wishlist = ev.target.value;
        setApiData(newApiData);
    };

    const show_wishlist_progressbar = [
        GamePublicationWishlistState.CONFIGURATION_MARKETING,
        GamePublicationWishlistState.TESTING_MARKETING,
        GamePublicationWishlistState.DONE,
    ].includes(gameCommonInfo.game.publication_wishlist_state);

    useEffect(() => {
        if (apiData.forms_data) {
            const newApiData = { ...apiData };
            newApiData.forms_data.main.data.moder_comment = "";
            setApiData(newApiData);
        }
    }, [apiData.forms_data?.main.fields]);

    if (gameCommonInfo.game.publication_state === GamePublicationState.DONE) {
        return <AlreadyDoneModerationTextBlock />;
    }

    if (
        ![GamePublicationState.CONFIGURATION, GamePublicationState.UNPUBLISHED].includes(
            gameCommonInfo.game.publication_state
        ) &&
        gameCommonInfo.game.publication_wishlist_state !== GamePublicationWishlistState.DONE
    ) {
        return <AlreadyInModerationTextBlock />;
    }

    const pna = buildPNA(apiData.publication_not_allowed, true);

    return (
        <>
            <PublicationNotAllowedBlock pna={pna} />
            {show_wishlist_progressbar && (
                <div className="d-flex">
                    <ModeratorPublicationMethodLabel branch={branch} />
                    <div className="w-100">
                        <GamePublicationWishlistStateProgressBar />
                    </div>
                    <ModeratorCancelPublicationButton branch={branch} />
                </div>
            )}

            <Form onSubmit={onModerSubmit}>
                {gameCommonInfo.game.publication_wishlist_state === GamePublicationWishlistState.CONFIGURATION && (
                    <>
                        {commonInfo.account.is_super && (
                            <div className="my-2">
                                <div>
                                    <span style={{ display: "inline-block" }}>
                                        <DevForm.Checkbox
                                            type="radio"
                                            prefix="main"
                                            id="publication_type_auto"
                                            name="publication_type_wishlist"
                                            value="auto"
                                            checked={
                                                apiData.forms_data.main.data.publication_type_wishlist === "auto" ||
                                                !apiData.forms_data.main.data.publication_type_wishlist
                                            }
                                            label={t("Автоматическая публикация")}
                                            onChange={fldChange}
                                        />
                                    </span>{" "}
                                    <Hint tooltip={pta_hint} />
                                </div>
                                <div>
                                    <span style={{ display: "inline-block" }}>
                                        <DevForm.Checkbox
                                            type="radio"
                                            prefix="main"
                                            id="publication_type_manual"
                                            name="publication_type_wishlist"
                                            value="manual"
                                            checked={
                                                apiData.forms_data.main.data.publication_type_wishlist === "manual"
                                            }
                                            label={t("Ручная публикация")}
                                            onChange={fldChange}
                                        />
                                    </span>{" "}
                                    <Hint tooltip={ptm_hint} />
                                </div>
                            </div>
                        )}
                        <DevForm.Button variant="primary" type="submit" name="action" value="start_wishlist_moderation">
                            {t("Отправить на проверку")}
                        </DevForm.Button>
                    </>
                )}

                {gameCommonInfo.game.publication_wishlist_state === GamePublicationWishlistState.TESTING_MARKETING && (
                    <ModerationBlock onModerSubmit={onModerSubmit} branch={branch} />
                )}
            </Form>
            {gameCommonInfo.game.publication_wishlist_state === GamePublicationWishlistState.TESTING_MARKETING && (
                <>
                    <InfoTable step="mark" apiData={apiData} />
                    <ModeratorCommentsEmbedded gameId={gameCommonInfo.game.id} filter="marketing" />
                </>
            )}
            {gameCommonInfo.game.publication_wishlist_state === GamePublicationWishlistState.DONE && (
                <>
                    {commonInfo.account.is_super &&
                        apiData?.forms_data?.main?.data?.publication_type_wishlist === "manual" && (
                            <Form onSubmit={onModerSubmit}>
                                {wishlistMode(gameCommonInfo.game) && (
                                    <>
                                        <DevForm.Field apiData={apiData} prefix="main" name="date_launch_wishlist" />
                                        <DevForm.Button variant="primary" type="submit" name="save">
                                            {t("Сохранить")}
                                        </DevForm.Button>
                                    </>
                                )}
                            </Form>
                        )}
                    <InfoTable step="done" apiData={apiData} />
                </>
            )}
        </>
    );
}

function jiraTaskGenerator(gameId, gameType, gameName, gameAttributes, gameLabels, date_unpublished) {
    const pubType = "проекта";
    const serviceComment = document.getElementsByName("main-service_comment")[0].value;
    const jiraTaskTitle = encodeURIComponent(`Публикация ${pubType} - ${gameType} - ${gameName}`);
    let jiraTaskDesc = `Атрибуты: ${gameAttributes}`;
    if (gameLabels?.length) {
        jiraTaskDesc += `\nМетка: ${gameLabels.join(", ")}`;
    }
    jiraTaskDesc += `\n\n${serviceComment}`;
    if (date_unpublished) {
        jiraTaskDesc += `\n\n*Примечание: проект был снят с публикации ${date_unpublished} и подался еще раз.*`;
    }
    jiraTaskDesc = encodeURIComponent(jiraTaskDesc);
    const gameLink = `https://${window.location.hostname}/dev/games/${gameId}`;
    const jiraLink = `https://jira.vk.team/secure/CreateIssueDetails!init.jspa?pid=10271&issuetype=15800&components=110083&summary=${jiraTaskTitle}&description=${jiraTaskDesc}&priority=3&customfield_15946=${gameLink}`;
    window.open(jiraLink, "_blank", "noreferrer");
}

function getJiraTaskButton(modalAction, gameCommonInfo, translator) {
    // Не отображаем кнопку, есть кликнули "Не пройдено".
    if (modalAction === "moder_refused") {
        return;
    }

    // Отображаем кнопку только при технической модерации проекта. Техмодерация страницы пройдена автоматически,
    // поэтому кнопка для страницы не отображается.
    const mustShowButton =
        gameCommonInfo.game.publication_state === "testing" &&
        gameCommonInfo.game.publication_wishlist_state === "configuration";
    if (!mustShowButton) {
        return;
    }

    // Генерируем кнопку и ссылку в Jira
    const button = (
        <DevForm.Button
            variant="secondary"
            type="button"
            name="jiralink"
            onClick={() => {
                jiraTaskGenerator(
                    gameCommonInfo.game.id,
                    gameCommonInfo.game.type,
                    gameCommonInfo.game.base_locale_object.name,
                    gameCommonInfo.game.feature_flags,
                    gameCommonInfo.game_labels,
                    gameCommonInfo.game.date_unpublished
                );
            }}
        >
            {translator("Завести задачу в Jira")}
        </DevForm.Button>
    );
    return button;
}

function ModerationBlock({ onModerSubmit, branch }) {
    const { t } = useTranslation();
    const [modalInfo, setModalInfo] = useState(null);
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { apiData, setApiData } = useAppContext();

    // Определяем, на каком этапе модерации мы находимся
    const isMarketingModeration =
        gameCommonInfo.game.publication_state === GamePublicationState.TESTING_MARKETING ||
        gameCommonInfo.game.publication_wishlist_state === GamePublicationWishlistState.TESTING_MARKETING;

    const showModal = (ev) => {
        ev.preventDefault();
        const action = ev.target.value;
        let header = action === "moder_passed" ? t("Подтверждение пройденной") : t("Подтверждение непройденной");
        if (branch === "project") {
            if (gameCommonInfo.game.publication_state === GamePublicationState.TESTING) {
                header += " " + t("технической модерации проекта");
            } else if (gameCommonInfo.game.publication_state === GamePublicationState.TESTING_MARKETING) {
                header += " " + t("маркетинговой модерации проекта");
            }
        } else if (branch === "wishlist") {
            header += " " + t("маркетинговой модерации страницы");
        }
        setModalInfo({ action, header });
    };

    return (
        <div className="mt-3">
            {gameCommonInfo.game.developer_comment && (
                <div className="dmg-callout dmg-callout-info">
                    <h6>{t("Комментарий разработчика")}</h6>
                    <div className="font-weight-light show-wrap-text">
                        {parseAndLinkify(gameCommonInfo.game.developer_comment)}
                    </div>
                </div>
            )}
            <div className="shadow p-3 mt-2 bg-light">
                <RenderForm bareForm={true} hideFields={["service_comment"]} />
                {/* Поля модерации с комментариями - только для маркетинговой модерации */}
                {isMarketingModeration && apiData.moderation_fields && (
                    <ModerationFieldsList
                        fields={apiData.moderation_fields.fields}
                        inReview={apiData.moderation_fields.in_review || false}
                        onFieldCommentChange={(fieldName, comment) => {
                            const newApiData = { ...apiData };
                            if (!newApiData.moderation_fields) {
                                newApiData.moderation_fields = { fields: [] };
                            }
                            const fieldIndex = newApiData.moderation_fields.fields.findIndex(
                                (f) => f.name === fieldName
                            );
                            if (fieldIndex >= 0) {
                                newApiData.moderation_fields.fields[fieldIndex].comment = comment;
                            }
                            if (newApiData.forms_data?.main?.data) {
                                newApiData.forms_data.main.data[`field-comment-${fieldName}`] = comment;
                            }
                            setApiData(newApiData);
                        }}
                    />
                )}
                <div className="d-flex justify-content-between mt-3">
                    <Button variant="warning" name="action" value="moder_refused" onClick={showModal}>
                        <Awesome type="arrow-left" /> {t("Не пройдено")}
                    </Button>
                    <Button variant="success" value="moder_passed" onClick={showModal}>
                        {t("Пройдено")} <Awesome type="arrow-right" />
                    </Button>
                </div>
            </div>
            {modalInfo && (
                <DevModal
                    show={modalInfo}
                    onHide={() => setModalInfo(null)}
                    header={modalInfo.header}
                    body={
                        gameCommonInfo.game.publication_state === GamePublicationState.TESTING ||
                        gameCommonInfo.game.publication_wishlist_state ===
                            GamePublicationWishlistState.TESTING_MARKETING ||
                        gameCommonInfo.game.publication_state === GamePublicationState.TESTING_MARKETING ? (
                            <DevForm.Field
                                id={"modal_service_comment"}
                                prefix="main"
                                name="service_comment"
                                hideLabel={true}
                                asRow={false}
                            />
                        ) : (
                            t("Подтвердите свое действие")
                        )
                    }
                    footer={
                        <>
                            <DevForm.Button
                                variant="primary"
                                name="action"
                                value={modalInfo.action}
                                onClick={(ev) => {
                                    onModerSubmit(ev);
                                    setModalInfo(null);
                                }}
                            >
                                {t("Подтверждаю")}
                            </DevForm.Button>
                            {getJiraTaskButton(modalInfo.action, gameCommonInfo, t)}
                        </>
                    }
                />
            )}
        </div>
    );
}

function ModeratorPublicationMethodLabel({ branch }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    return (
        <span className="mr-2 my-auto">
            {gameCommonInfo.game.manual_publication_data && gameCommonInfo.game.manual_publication_data[branch] ? (
                <Awesome type="hand-stop-o" color="green" tooltip={t("Ручная публикация")} />
            ) : (
                <Awesome type="car" color="green" tooltip={t("Автоматическая публикация")} />
            )}
        </span>
    );
}

function ModeratorCancelPublicationButton({ branch }) {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const { api_post } = useRequest();

    const revalidator = useRevalidator();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    const cancelModeration = (ev) => {
        api_post(ev, url_reverse("api:game:publication:moderator", { game_id: gameCommonInfo.game.id }), {
            params: {
                action: "cancel_moderation",
                branch,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    setShowModal(false);
                    scroll_to_top();
                    revalidator.revalidate();
                }
            },
        });
    };

    return (
        <>
            <span className="ml-2 my-auto" style={{ cursor: "pointer" }}>
                <Button variant="danger" size="sm" onClick={() => setShowModal(true)}>
                    <Awesome type="ban" tooltip={t("Снять с модерации")} />
                </Button>
            </span>
            <DevModal
                show={showModal}
                onHide={() => setShowModal(false)}
                header={t("Снять с модерации")}
                body={t("Подтвердите свое действие")}
                footer={
                    <DevForm.Button variant="primary" name="cancel_moderation" onClick={cancelModeration}>
                        {t("Подтверждаю")}
                    </DevForm.Button>
                }
            />
        </>
    );
}

function DeveloperScreen({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(url_reverse("api:game:publication:developer", { game_id: gameId }));
    const revalidator = useRevalidator();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const show_tab = useActiveTab(gameCommonInfo.game);
    const onFormSubmit = useSubmit();

    if (!apiData) return;

    const onDeveloperSubmit = (ev) => {
        onFormSubmit(ev, {
            onOK: (resp) => {
                setApiData(resp);
                revalidator.revalidate();
            },
        });
    };

    return (
        <CommonCard header={header}>
            {show_tab ? (
                <Tabs
                    defaultActiveKey={show_tab}
                    transition={false}
                    className="react-bootstrap-tabs-fix"
                    unmountOnExit={true}
                >
                    <Tab eventKey="project" title={t("Публикация проекта")}>
                        <DeveloperProjectPublicationBlock onDeveloperSubmit={onDeveloperSubmit} with_wishlist={true} />
                    </Tab>
                    <Tab eventKey="wishlist" title={t("Публикация страницы")}>
                        <PageWishListTextBlock />
                        <DeveloperWishlistPublicationBlock apiData={apiData} />
                    </Tab>
                </Tabs>
            ) : (
                <DeveloperProjectPublicationBlock onDeveloperSubmit={onDeveloperSubmit} />
            )}
        </CommonCard>
    );
}

function DeveloperProjectPublicationBlock({ onDeveloperSubmit, with_wishlist = false }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const show_tab = useActiveTab(gameCommonInfo.game);

    const branch = "project";
    const pta_hint = t(
        'Проект будет опубликован сразу после прохождения проверки. В поле "Дата выхода игры" будет проставлена дата фактической публикации проекта на площадке VK Play.'
    );
    const ptm_hint = t("Точные дату и время релиза нужно выбрать самостоятельно после проверки.");

    useEffect(() => {
        if (apiData.forms_data) {
            const newApiData = { ...apiData };
            newApiData.forms_data.main.data.i_wanna_wishlist =
                Boolean(apiData.forms_data.main.data.date_launch_wishlist) ||
                Boolean(apiData.forms_data.main.errors.date_launch_wishlist);
            setApiData(newApiData);
        }
    }, [apiData.forms_data?.main.data]);

    useEffect(() => {
        if (apiData.forms_data && !apiData.forms_data.main.data.i_wanna_wishlist) {
            const newApiData = { ...apiData };
            delete newApiData.forms_data.main.data.date_launch_wishlist;
            setApiData(newApiData);
        }
    }, [apiData.forms_data?.main.data.i_wanna_wishlist]);

    const now = new Date();
    const date_launch = gameCommonInfo.game.date_launch ? new Date(gameCommonInfo.game.date_launch) : null;
    const pna = buildPNA(apiData.publication_not_allowed, false);
    const denied = Object.values(pna).filter((val) => Object.keys(val).length > 0 && !val.is_active).length > 0;

    return (
        <>
            {[
                GamePublicationState.CONFIGURATION,
                GamePublicationState.CONFIGURATION_MARKETING,
                GamePublicationState.UNPUBLISHED,
            ].includes(gameCommonInfo.game.publication_state) &&
                ([GamePublicationWishlistState.CONFIGURATION, GamePublicationWishlistState.DONE].includes(
                    gameCommonInfo.game.publication_wishlist_state
                ) ? (
                    <>
                        {with_wishlist && <ProjectWishListTextBlock />}
                        {!show_tab && <ProjectNoWishListTextBlock />}
                        <GamePublicationStateProgressBar />
                        <PublicationNotAllowedBlock pna={pna} />
                        {/* Отображение комментариев модератора после отказа */}
                        {apiData.moderation_fields_for_developer && (
                            <ModeratorCommentsForDeveloper moderationFields={apiData.moderation_fields_for_developer} />
                        )}
                        <DeveloperSendToModerationBlock
                            denied={denied}
                            branch={branch}
                            pta_hint={pta_hint}
                            ptm_hint={ptm_hint}
                        />
                    </>
                ) : (
                    <AlreadyInModerationTextBlock />
                ))}

            {[GamePublicationState.TESTING, GamePublicationState.TESTING_MARKETING].includes(
                gameCommonInfo.game.publication_state
            ) && (
                <>
                    <GamePublicationStateProgressBar />
                    <DeveloperCancelPublicationButton branch={branch} />
                </>
            )}

            {gameCommonInfo.game.publication_state === GamePublicationState.DONE && (
                <>
                    <GamePublicationStateProgressBar />

                    {(!date_launch || now < date_launch) && (
                        <Form onSubmit={onDeveloperSubmit}>
                            <DevForm.Field prefix="main" name="date_launch" />
                            {wishlistMode(gameCommonInfo.game) && (
                                <>
                                    <DevForm.Field prefix="main" name="i_wanna_wishlist" />
                                    {apiData.forms_data.main.data.i_wanna_wishlist && (
                                        <DevForm.Field prefix="main" name="date_launch_wishlist" />
                                    )}
                                </>
                            )}
                            <DevForm.Button variant="primary" type="submit" name="save">
                                {t("Сохранить")}
                            </DevForm.Button>
                        </Form>
                    )}
                </>
            )}
        </>
    );
}

function DeveloperWishlistPublicationBlock({ apiData }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const branch = "wishlist";

    if (gameCommonInfo.game.publication_state === GamePublicationState.DONE) {
        return <AlreadyDoneModerationTextBlock />;
    }

    const now = new Date();
    const date_launch_wishlist = gameCommonInfo.game.date_launch_wishlist
        ? new Date(gameCommonInfo.game.date_launch_wishlist)
        : null;
    const pna = buildPNA(apiData.publication_not_allowed, true);
    const denied = Object.values(pna).filter((val) => Object.keys(val).length > 0 && !val.is_active).length > 0;

    return (
        <>
            {[
                GamePublicationWishlistState.CONFIGURATION,
                GamePublicationWishlistState.CONFIGURATION_MARKETING,
            ].includes(gameCommonInfo.game.publication_wishlist_state) &&
                ([GamePublicationState.CONFIGURATION, GamePublicationState.UNPUBLISHED].includes(
                    gameCommonInfo.game.publication_state
                ) ? (
                    <>
                        <GamePublicationWishlistStateProgressBar />
                        <PublicationNotAllowedBlock pna={pna} />
                        {/* Отображение комментариев модератора после отказа */}
                        {apiData.moderation_fields_for_developer && (
                            <ModeratorCommentsForDeveloper moderationFields={apiData.moderation_fields_for_developer} />
                        )}
                        <DeveloperSendToModerationBlock
                            denied={denied}
                            branch={branch}
                            pta_hint={t("Страница будет опубликована сразу после прохождения проверки.")}
                            ptm_hint={t("Точные дату и время релиза нужно выбрать самостоятельно после проверки.")}
                        />
                    </>
                ) : (
                    <AlreadyInModerationTextBlock />
                ))}

            {gameCommonInfo.game.publication_wishlist_state === GamePublicationWishlistState.TESTING_MARKETING && (
                <>
                    <GamePublicationWishlistStateProgressBar />
                    <DeveloperCancelPublicationButton branch={branch} />
                </>
            )}

            {gameCommonInfo.game.publication_wishlist_state === GamePublicationWishlistState.DONE && (
                <>
                    <GamePublicationWishlistStateProgressBar />
                    {gameCommonInfo.game.manual_publication_data &&
                        (!date_launch_wishlist || now < date_launch_wishlist) && <RenderForm />}
                </>
            )}
        </>
    );
}

function DeveloperSendToModerationBlock({ denied, pta_hint, ptm_hint, branch }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const [fld, setFld] = useState({ publication_type: "auto" });
    const { api_post } = useRequest();
    const [monetModelModal, setMonetModelModal] = useState(false);

    const revalidator = useRevalidator();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { game } = gameCommonInfo;
    const [error, setError] = useState([]);

    const gameTitle = game.base_locale_object.name;
    const monetModel = game.monetization_model;
    const status = game.is_sellable ? t("Платный") : t("Бесплатный");

    const fldChange = (ev) => {
        const newFld = { ...fld };
        newFld[ev.target.name] = ev.target.value;
        setFld(newFld);
    };

    const sendToModeration = (ev, action) => {
        api_post(ev, url_reverse("api:game:publication:developer", { game_id: game.id }), {
            params: {
                action,
                branch,
                publication_type: fld.publication_type,
                developer_comment: fld.developer_comment,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    scroll_to_top();

                    const newApiData = { ...apiData };
                    newApiData.monetization_model_need_approve = false;
                    setApiData(newApiData);

                    revalidator.revalidate();
                } else {
                    setError(resp.forms_data.main.non_field_errors);
                }
            },
        });
    };

    const handleClick = (ev) => {
        if (apiData.monetization_model_need_approve) {
            setMonetModelModal(ev.target.value);
        } else {
            sendToModeration(ev, ev.target.value);
        }
    };

    const errorMessage = (errorForm) => {
        const non_field_errors = [];
        if (errorForm && errorForm.length > 0) {
            errorForm.map((err, i) =>
                non_field_errors.push(
                    <div key={`non_field_errors${i}`} className="text-danger">
                        {parse(err)}
                    </div>
                )
            );
        }
        if (non_field_errors.length) {
            return (
                <>
                    <div className="error_collection">{non_field_errors}</div>
                </>
            );
        }
        return null;
    };

    return (
        <>
            {game.moder_comment && (
                <div className="dmg-callout dmg-callout-warning">
                    <h6>{t("Комментарий модератора")}</h6>
                    <div className="font-weight-light show-wrap-text">
                        {game.moder_comment.split("\n").map((o, i) => {
                            return <div key={`mc_${i}`}>{parseAndLinkify(o + "\n")}</div>;
                        })}
                    </div>
                </div>
            )}
            <div className="mt-2">
                {errorMessage(error)}
                <DevForm.TextArea
                    name="developer_comment"
                    value={fld.developer_comment}
                    placeholder={t("Комментарий разработчика")}
                    rows={5}
                    onChange={fldChange}
                />
                {game.publication_state === GamePublicationState.CONFIGURATION_MARKETING ||
                game.publication_wishlist_state === GamePublicationState.CONFIGURATION_MARKETING ? (
                    <div className="mt-2">
                        <DevForm.Button
                            variant="primary"
                            name="action"
                            value="to_testing_marketing"
                            disabled={denied}
                            onClick={handleClick}
                        >
                            {t("Вернуть на проверку")}
                        </DevForm.Button>
                        <DeveloperCancelPublicationButton branch={branch} />
                    </div>
                ) : (
                    <>
                        <div className="my-2">
                            <div>
                                <span style={{ display: "inline-block" }}>
                                    <DevForm.Checkbox
                                        type="radio"
                                        id="publication_type_auto"
                                        name="publication_type"
                                        value="auto"
                                        checked={fld.publication_type === "auto"}
                                        label={t("Автоматическая публикация")}
                                        onChange={fldChange}
                                    />
                                </span>{" "}
                                <Hint tooltip={pta_hint} />
                            </div>
                            <div>
                                <span style={{ display: "inline-block" }}>
                                    <DevForm.Checkbox
                                        type="radio"
                                        id="publication_type_manual"
                                        name="publication_type"
                                        value="manual"
                                        checked={fld.publication_type === "manual"}
                                        label={t("Ручная публикация")}
                                        onChange={fldChange}
                                    />
                                </span>{" "}
                                <Hint tooltip={ptm_hint} />
                            </div>
                        </div>
                        <DevForm.Button
                            variant="primary"
                            name="action"
                            value="to_testing"
                            disabled={denied}
                            onClick={handleClick}
                        >
                            {t("Отправить на проверку")}
                        </DevForm.Button>
                    </>
                )}
            </div>
            <DevModal
                size="lg"
                show={monetModelModal}
                onHide={() => setMonetModelModal(false)}
                header={t("Подтверждение данных перед отправкой на модерацию")}
                body={
                    <Trans>
                        Проект {{ gameTitle }} — {{ status }}. <br />
                        Модель монетизации — {{ monetModel }}. <br />
                        Адрес страницы меняться не будет. <br />
                        Это проект основной игры, а не его демоверсия. Я знаю, что для публикации демоверсии нужен
                        отдельный проект (
                        <a href="https://documentation.vkplay.ru/p2p_vkp/p2p_demo" target="_blank">
                            документация
                        </a>
                        ).
                    </Trans>
                }
                footer={
                    <>
                        <DevForm.Button variant="secondary" name="cancel" onClick={() => setMonetModelModal(false)}>
                            {t("Отмена")}
                        </DevForm.Button>
                        <DevForm.Button
                            variant="primary"
                            name="confirm_monetization_model"
                            onClick={(ev) => {
                                setMonetModelModal(false);
                                sendToModeration(ev, monetModelModal);
                            }}
                        >
                            {t("Подтвердить")}
                        </DevForm.Button>
                    </>
                }
            />
        </>
    );
}

function DeveloperCancelPublicationButton({ branch }) {
    const { t } = useTranslation();
    const revalidator = useRevalidator();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { api_post } = useRequest();

    const cancelModeration = (ev) => {
        api_post(ev, url_reverse("api:game:publication:developer", { game_id: gameCommonInfo.game.id }), {
            params: {
                action: "to_configuration",
                branch,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    scroll_to_top();
                    revalidator.revalidate();
                }
            },
        });
    };

    return (
        <DevForm.Button variant="danger" name="cancel_moderation" onClick={cancelModeration} className="float-right">
            {t("Снять с модерации")}
        </DevForm.Button>
    );
}

function useActiveTab(game) {
    if (wishlistMode(game)) {
        if (
            game.publication_state === GamePublicationState.CONFIGURATION &&
            game.publication_wishlist_state === GamePublicationWishlistState.CONFIGURATION
        ) {
            return "project";
        }
        return [GamePublicationWishlistState.CONFIGURATION, GamePublicationWishlistState.DONE].includes(
            game.publication_wishlist_state
        ) && game.publication_state !== GamePublicationState.CONFIGURATION
            ? "project"
            : "wishlist";
    }
}

function ProjectNoWishListTextBlock() {
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    return (
        !gameCommonInfo.game.is_disabled && (
            <>
                <div className="dmg-callout dmg-callout-info">
                    {gameCommonInfo.game.is_browser && (
                        <>
                            <Trans>
                                ✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.
                            </Trans>
                            <br />
                            <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                            <br />
                            <Trans>
                                ℹ️{" "}
                                <a
                                    target="_blank"
                                    href="https://documentation.vkplay.ru/p2p_vkp/p2p_publish_vkp#checklist"
                                >
                                    Подробнее о модерации.
                                </a>
                            </Trans>
                            <br />
                            <Trans>🎮 После публикации на странице появится кнопка «Играть».</Trans>
                        </>
                    )}
                    {gameCommonInfo.game.is_demo && (
                        <>
                            <Trans>
                                ℹ️{" "}
                                <a target="_blank" href="https://documentation.vkplay.ru/p2p_vkp/p2p_demo">
                                    Документация по демо
                                </a>
                                .
                            </Trans>
                            <br />
                            <Trans>
                                ✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.
                            </Trans>
                            <br />
                            <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                            <br />
                            <Trans>🎮 После публикации демо появится на странице игры.</Trans>
                        </>
                    )}
                    {gameCommonInfo.game.is_bundle && (
                        <>
                            <Trans>
                                ℹ️{" "}
                                <a target="_blank" href="https://documentation.vkplay.ru/p2p_vkp/bundle">
                                    Документация по наборам
                                </a>
                                .
                            </Trans>
                            <br />
                            <Trans>
                                ✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.
                            </Trans>
                            <br />
                            <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                            <br />
                            <Trans>🛒 После публикации набор появится на странице и его можно будет купить.</Trans>
                        </>
                    )}
                </div>
            </>
        )
    );
}

function ProjectWishListTextBlock() {
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const clientNoDlcBundleDemo = [GameFeatureFlags.DLC, GameFeatureFlags.BUNDLE, GameFeatureFlags.DEMO].every(
        (key) => !gameCommonInfo.game.feature_flags.includes(key)
    );
    const client = gameCommonInfo.game.feature_flags.includes(GameFeatureFlags.CLIENT);

    return (
        <>
            {client && clientNoDlcBundleDemo && (
                <>
                    <div className="dmg-callout dmg-callout-info">
                        <Trans>
                            ✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.
                        </Trans>
                        <br />
                        <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                        <br />
                        <Trans>
                            ℹ️{" "}
                            <a target="_blank" href="https://documentation.vkplay.ru/p2p_vkp/p2p_publish_vkp#checklist">
                                Подробнее о модерации.
                            </a>
                        </Trans>
                        <br />
                        <Trans>🎮 После публикации на странице появится кнопка «Скачать/Купить».</Trans>
                        <br />
                        <Trans>
                            📌Перед этим можно выпустить отдельно страницу — с кнопкой «Добавить в избранное».
                        </Trans>
                        <br />
                        <Trans>
                            📄 Нет билда? Отправьте на проверку оформление — в соседней вкладке «Публикация страницы».
                        </Trans>
                    </div>
                </>
            )}
            {client && gameCommonInfo.game?.dlc?.kind === GameRelationshipKind.DLC_ITEM && (
                <div className="dmg-callout dmg-callout-info">
                    <Trans>
                        ℹ️{" "}
                        <a target="_blank" href="https://documentation.vkplay.ru/p2p_vkp/dlc">
                            Документация по DLC
                        </a>
                        .
                    </Trans>
                    <br />
                    <Trans>✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.</Trans>
                    <br />
                    <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                    <br />
                    <Trans>🎮 После публикации — DLC появится на странице с кнопкой «Купить/Добавить бесплатно».</Trans>
                    <br />
                    <Trans>
                        📄 DLC ещё не готово? Отправьте на проверку его оформление — в соседней вкладке «Публикация
                        страницы».
                    </Trans>
                </div>
            )}
            {client && gameCommonInfo.game?.dlc?.kind === GameRelationshipKind.DLC_CONTENT && (
                <div className="dmg-callout dmg-callout-info">
                    <Trans>
                        ℹ️{" "}
                        <a target="_blank" href="https://documentation.vkplay.ru/p2p_vkp/dlc">
                            Документация по DLC
                        </a>
                        .
                    </Trans>
                    <br />
                    <Trans>✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.</Trans>
                    <br />
                    <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                    <br />
                    <Trans>🎮 После публикации — DLC появится на странице с кнопкой «Купить/Добавить бесплатно».</Trans>
                    <br />
                    <Trans>
                        📄 DLC ещё не готово? Отправьте на проверку его оформление — в соседней вкладке «Публикация
                        страницы».
                    </Trans>
                </div>
            )}
        </>
    );
}

function PageWishListTextBlock() {
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const clientNoDlcBundleDemo = [GameFeatureFlags.DLC, GameFeatureFlags.BUNDLE, GameFeatureFlags.DEMO].every(
        (key) => !gameCommonInfo.game.feature_flags.includes(key)
    );
    const client = gameCommonInfo.game.feature_flags.includes(GameFeatureFlags.CLIENT);

    return (
        <>
            {client && clientNoDlcBundleDemo && (
                <div className="dmg-callout dmg-callout-info">
                    <Trans>✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.</Trans>
                    <br />
                    <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                    <br />
                    <Trans>
                        ℹ️{" "}
                        <a target="_blank" href="https://documentation.vkplay.ru/p2p_vkp/p2p_publish_vkp#checklist2">
                            Подробнее о модерации.
                        </a>
                    </Trans>
                    <br />
                    <Trans>
                        🌟 После публикации страница станет видна пользователям. На ней появится кнопка «Добавить в
                        избранное».
                    </Trans>
                    <br />
                    <Trans>📄 Билд можно отправить на проверку в соседней вкладке — «Публикация проекта».</Trans>
                </div>
            )}
            {client && gameCommonInfo.game?.dlc?.kind === GameRelationshipKind.DLC_ITEM && (
                <div className="dmg-callout dmg-callout-info">
                    <Trans>
                        ℹ️{" "}
                        <a target="_blank" href="https://documentation.vkplay.ru/p2p_vkp/dlc">
                            Документация по DLC
                        </a>
                        .
                    </Trans>
                    <br />
                    <Trans>✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.</Trans>
                    <br />
                    <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                    <br />
                    <Trans>
                        🌟 После публикации страница станет видна пользователям. На ней появится кнопка «Добавить в
                        избранное».
                    </Trans>
                    <br />
                    <Trans>📄 DLC можно отправить на проверку в соседней вкладке — «Публикация проекта».</Trans>
                </div>
            )}
            {client && gameCommonInfo.game?.dlc?.kind === GameRelationshipKind.DLC_CONTENT && (
                <div className="dmg-callout dmg-callout-info">
                    <Trans>
                        ℹ️{" "}
                        <a target="_blank" href="https://documentation.vkplay.ru/p2p_vkp/dlc">
                            Документация по DLC
                        </a>
                        .
                    </Trans>
                    <br />
                    <Trans>✅ Заполните поля по чек-листу — и кнопка «Отправить на проверку» станет активной.</Trans>
                    <br />
                    <Trans>🚀 Выберете способ публикации: «Ручной» или «Автоматический».</Trans>
                    <br />
                    <Trans>
                        🌟 После публикации страница станет видна пользователям. На ней появится кнопка «Добавить в
                        избранное».
                    </Trans>
                    <br />
                    <Trans>📄 DLC можно отправить на проверку в соседней вкладке — «Публикация проекта».</Trans>
                </div>
            )}
        </>
    );
}

function AlreadyInModerationTextBlock() {
    return (
        <p className="mt-2 text-danger">
            <Trans>
                Проект уже находится на проверке, пожалуйста дождитесь ее завершения или нажмите на кнопку "Снять с
                модерации"
            </Trans>
        </p>
    );
}

function AlreadyDoneModerationTextBlock() {
    return (
        <p className="mt-2">
            <Trans>Проверка страницы недоступна т.к. проект уже прошел модерацию</Trans>
        </p>
    );
}

// поле → { group, order } — группировка/порядок чек-листа публикации; ключ поля
// извлекается из url (после "#"), не из локализованного name — не зависим от языка.
const PUBLICATION_FIELD_META = {
    name: { group: "meta", order: 0 },
    main_genre: { group: "meta", order: 1 },
    genre: { group: "meta", order: 2 },
    long_descr: { group: "meta", order: 3 },
    about_text: { group: "meta", order: 4 },
    date_release_estimated: { group: "meta", order: 5 },
    localizers_data: { group: "meta", order: 6 },
    iframe_landing_url: { group: "meta", order: 7 },
    support_url: { group: "meta", order: 8 },
    age_limit: { group: "meta", order: 9 },
    m2m_gamedevs: { group: "meta", order: 10 },
    m2m_publishers: { group: "meta", order: 11 },
    picture_horizontal: { group: "images", order: 0 },
    picture_vertical: { group: "images", order: 1 },
    picture_bundle: { group: "images", order: 2 },
    art: { group: "images", order: 3 },
    sys_req_web_data: { group: "sysreq", order: 0 },
    platform: { group: "distrib", order: 0 },
};

// поля, скрытые из чек-листа полностью (по-прежнему участвуют в is_active/denied)
const PUBLICATION_HIDDEN_FIELDS = new Set(["feature_flags"]);

function publicationFieldKey(url) {
    return url.split("#").pop();
}

function PublicationNotAllowedBlock({ pna }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (!Object.keys(pna || {}).length) {
        return;
    }

    // подзаголовки чек-листа: id + label вместе, порядок отображения — порядок в массиве.
    // t() зовём с литералами (не из PUBLICATION_HIDDEN_FIELDS/переменной) — иначе
    // i18next-parser не найдёт строку статически и сотрёт перевод из локали.
    const PUBLICATION_GROUPS = [
        { id: "payment", label: t("Платежная информация") },
        { id: "meta", label: t("Метаданные") },
        { id: "images", label: t("Изображения") },
        { id: "screenshots", label: t("Скриншоты и видео") },
        { id: "sysreq", label: t("Системные требования") },
        { id: "countries", label: t("Страны") },
        { id: "distrib", label: t("Дистрибутивы") },
        { id: "parent", label: t("Родительский проект") },
    ];

    // чекбокс — только индикатор (не форма), поэтому рисуем символ вместо нативного
    // disabled-инпута: браузеры игнорируют accent-color на disabled-чекбоксах.
    const drawCheck = (obj, label) => {
        if (!obj || !obj.url) {
            return null;
        }
        return (
            <div className="publication-checklist-item">
                <span
                    className="publication-checklist-item-mark"
                    role="img"
                    aria-label={obj.is_active ? t("выполнено") : t("не выполнено")}
                >
                    {obj.is_active ? "✅" : "☐"}
                </span>
                <a href={obj.url}>{label}</a>
            </div>
        );
    };

    // плоский список { group, order, node } — собираем из всех разделов pna,
    // потом группируем по подзаголовкам и сортируем внутри группы.
    const items = [];

    if (pna.bad_developer) {
        items.push({ group: "payment", order: 0, node: drawCheck(pna.bad_developer, t("Финансовая модерация")) });
    }
    if (pna.prices) {
        items.push({ group: "payment", order: 1, node: drawCheck(pna.prices, t("Заполните цены")) });
    }

    // поля основного раздела (main). Поля родителя (parent.field_info) по ТЗ у дочерних
    // проектов индивидуально не показываем — ниже вместо этого добавляем один пункт на
    // первое незаполненное поле родителя, чтобы блокировка отправки не была немой.
    let mainVisibleFieldsCount = 0;
    (pna.main?.field_info || []).forEach((f) => {
        const key = publicationFieldKey(f.url);
        if (PUBLICATION_HIDDEN_FIELDS.has(key)) {
            return;
        }
        mainVisibleFieldsCount += 1;
        const meta = PUBLICATION_FIELD_META[key] || { group: "meta", order: 99 };
        // t() тут — литерал, а не meta.labelOverride: иначе i18next-parser не найдёт
        // строку статически и сотрёт перевод из локали.
        const label = key === "age_limit" ? t("Возрастной рейтинг") : f.name;
        items.push({ group: meta.group, order: meta.order, node: drawCheck(f, label) });
    });
    // если всё, что блокирует публикацию в main, скрыто из чек-листа (сейчас — только
    // feature_flags), пользователь не должен остаться без объяснения блокировки.
    if (pna.main && !pna.main.is_active && mainVisibleFieldsCount === 0) {
        items.push({ group: "meta", order: -1, node: drawCheck(pna.main, t("Заполните обязательные поля")) });
    }

    if (pna.screenshots) {
        items.push({
            group: "screenshots",
            order: 0,
            node: drawCheck(
                pna.screenshots,
                t("От 6 скриншотов в {{locale}} локализацию", {
                    locale: gameCommonInfo.game.base_locale.split("_")[0].toUpperCase(),
                })
            ),
        });
    }
    if (pna.screenshots_other_loc) {
        items.push({
            group: "screenshots",
            order: 1,
            node: drawCheck(
                pna.screenshots_other_loc,
                t("От 6 скриншотов в {{locale}} локализацию", {
                    locale: pna.screenshots_other_loc.locale.split("_")[0].toUpperCase(),
                })
            ),
        });
    }
    if (pna.countries) {
        items.push({ group: "countries", order: 0, node: drawCheck(pna.countries, t("Страны")) });
    }
    if (pna.client) {
        items.push({ group: "distrib", order: 1, node: drawCheck(pna.client, t("Загрузить билд игры в тест")) });
    }
    if (pna.live_client) {
        items.push({ group: "distrib", order: 2, node: drawCheck(pna.live_client, t("Перевести билд игры в Live")) });
    }
    if (pna.parent && !pna.parent.is_active) {
        const firstBadParentField = (pna.parent.field_info || []).find((f) => !f.is_active);
        items.push({
            group: "parent",
            order: 0,
            node: drawCheck(
                firstBadParentField || pna.parent,
                t("Заполните основные свойства в родительском проекте:")
            ),
        });
    }
    if (pna.bad_parent) {
        items.push({
            group: "parent",
            order: 1,
            node: drawCheck(pna.bad_parent, t("Родительский проект (или его страница) прошёл модерацию")),
        });
    }

    const grouped = PUBLICATION_GROUPS.map(({ id, label }) => ({
        group: id,
        label,
        items: items.filter((it) => it.group === id && it.node).sort((a, b) => a.order - b.order),
    })).filter((g) => g.items.length > 0);

    if (!grouped.length && !pna.publish_ban) {
        return null;
    }

    return (
        <div className="publication-checklist">
            <h5>{t("Обязательные настройки")}</h5>
            <div className="text-muted mb-3">{t("находятся в следующих разделах и вкладках:")}</div>
            {pna.publish_ban && (
                <div className="text-danger mb-3">
                    <Trans>
                        Возможность отправки игр на проверку ограничена, пожалуйста, свяжитесь с командой модерации в
                        чате или написав письмо на <a href="mailto:integration@vk.team">integration@vk.team</a>
                    </Trans>
                </div>
            )}
            {grouped.map(({ group, label, items: groupItems }) => (
                <div key={group} className="publication-checklist-group">
                    <div className="publication-checklist-group-title">{label}</div>
                    {groupItems.map((it, idx) => (
                        <div key={idx}>{it.node}</div>
                    ))}
                </div>
            ))}
        </div>
    );
}
